import type { PlayerEvents } from '../player_events';
import type { PlayerStore } from '../state/player_store.js';
import { SubscribersList } from '../state/subscribers_list.js';
import type { ContentType, PlaylistItem } from '../types.js';
import { capitalize } from '../utils/capitalize.js';

export type BasePlayerOptions<Container extends HTMLElement> = {
  store: PlayerStore;
  playerEvents: PlayerEvents;
  container: Container;
};

export abstract class BasePlayer<Container extends HTMLElement> {
  protected containerElement: Container;

  #containerObserver: ResizeObserver | undefined;

  protected subscribersList = new SubscribersList();

  protected store: PlayerStore;

  protected playerEvents: PlayerEvents;

  playerType: ContentType = 'unknown';

  protected constructor({
    store,
    playerEvents,
    container,
  }: BasePlayerOptions<Container>) {
    this.store = store;
    this.playerEvents = playerEvents;
    this.containerElement = container;
  }

  async init(): Promise<void> {
    this.containerElement.setAttribute(
      'aria-label',
      `${capitalize(this.playerType)} player`,
    );
    this.containerElement.setAttribute('role', 'region');

    const currentSize = this.containerElement.getBoundingClientRect();
    // Ignore a zero in either dimension, like the observer below: the store is
    // shared across players, so a container that is detached or not laid out
    // yet would otherwise overwrite a usable size
    if (currentSize.width !== 0 && currentSize.height !== 0) {
      this.store.size.setContainerSize({
        width: currentSize.width,
        height: currentSize.height,
      });
    }
    this.#containerObserver = new ResizeObserver(
      (entries: ResizeObserverEntry[]) => {
        const containerWidth = entries[0].contentBoxSize[0].inlineSize;
        const containerHeight = entries[0].contentBoxSize[0].blockSize;
        if (containerWidth === 0 || containerHeight === 0) {
          // Ignore zero size
          return;
        }
        this.store.size.setContainerSize({
          width: containerWidth,
          height: containerHeight,
        });
      },
    );
    this.#containerObserver.observe(this.containerElement);
  }

  async destroy() {
    this.subscribersList.unsubscribeAll();
    this.#containerObserver?.disconnect();
    this.#containerObserver = undefined;
  }

  abstract load(playlistItem: PlaylistItem): Promise<void>;

  play(): void {}

  pause(): void {}

  async requestFullscreen(): Promise<void> {}
}
